import type { Thought } from "../components/ThoughtCard";

/**
 * Shared query plumbing for the `thoughts` table.
 *
 * Every read path (hydrate, infinite scroll, "load everything" for the map,
 * search, export) used to hand-roll its own column list, cursor and row->Thought
 * mapping. They drifted: one paged, one truncated at a fixed `.range()`, one
 * sorted on a non-unique key. The pure helpers here are the single definition of
 * each, so a fix lands everywhere at once and can be unit-tested without a
 * network or a Supabase client.
 */

/** The exact column set the UI's `Thought` needs. Keep in step with `rowToThought`. */
export const THOUGHT_COLUMNS =
  "id,title,body,source_url,source_title,labels,created_at,updated_at,is_public,visibility,source_card_id,image_path,notes,link_preview";

/**
 * Rows per request when walking a table to completion.
 *
 * Deliberately well under PostgREST's `db-max-rows` (1000 by default). Paging
 * stops on a short page, so a page size at or above the server's ceiling makes
 * a *capped* response indistinguishable from a final one — the walk would end
 * early and silently, which is precisely the bug class this module exists to
 * prevent. Staying below the ceiling means a full page always means "ask again".
 */
export const PAGE_SIZE = 500;

/** Hard ceiling on rows returned by a single search. Hitting it is surfaced in
 *  the UI ("narrow your search") rather than silently truncating. */
export const SEARCH_LIMIT = 200;

/** A keyset position in the (created_at DESC, id DESC) ordering. */
export interface ThoughtCursor {
  created_at: string;
  id: string;
}

/**
 * Last real row in a loaded page, to resume paging after.
 *
 * Optimistic cards still carrying a `temp-` id have no server row, so they can
 * never be a valid cursor — walk back past them. Returns undefined when there
 * is nothing to resume from (empty list, or nothing but optimistic cards).
 */
export function cursorFrom(thoughts: Pick<Thought, "id" | "created_at">[]): ThoughtCursor | undefined {
  for (let i = thoughts.length - 1; i >= 0; i--) {
    const t = thoughts[i];
    if (!isOptimisticId(t.id)) return { created_at: t.created_at, id: t.id };
  }
  return undefined;
}

/** Optimistic, client-only ids that have no corresponding server row yet. */
export function isOptimisticId(id: string): boolean {
  return id.startsWith("temp-");
}

/**
 * Quote a value for use inside a PostgREST `or=(...)` group.
 *
 * Commas, parentheses and dots are structural in that grammar, so any value
 * that might contain them has to be double-quoted; inside the quotes, `"` and
 * `\` escape with a backslash. Timestamps (`2026-01-01T00:00:00.123+00:00`)
 * contain dots and colons, so they need this even though they look innocuous.
 */
export function quoteFilterValue(value: string): string {
  return `"${value.replace(/[\\"]/g, (m) => `\\${m}`)}"`;
}

/**
 * The keyset predicate for "strictly after this cursor" in (created_at DESC, id DESC).
 *
 * A cursor on `created_at` alone silently skips every row sharing the boundary
 * timestamp — routine after a bulk import, where hundreds of rows land on the
 * same millisecond. The compound form takes the whole timestamp group and then
 * breaks the tie on `id`, which is unique, so no row can be straddled or
 * repeated. It must be paired with a matching two-key `.order()`.
 */
export function keysetFilter(cursor: ThoughtCursor): string {
  const at = quoteFilterValue(cursor.created_at);
  const id = quoteFilterValue(cursor.id);
  return `created_at.lt.${at},and(created_at.eq.${at},id.lt.${id})`;
}

/**
 * Build a `%…%` pattern that matches the query as a literal substring.
 *
 * `%` and `_` are LIKE wildcards and `\` is the escape character, so a user
 * searching for "100%" must not turn into a match-everything pattern.
 */
export function ilikePattern(query: string): string {
  const escaped = query.trim().replace(/[\\%_]/g, (m) => `\\${m}`);
  return `%${escaped}%`;
}

/** Text columns the search scans. Labels are absent because they're `text[]`,
 *  which `ilike` cannot address — see `labelsMatching`. */
export const SEARCH_TEXT_COLUMNS = ["title", "body", "source_title"] as const;

/**
 * A complete `or=` group matching the query as a substring of any text column.
 *
 * The `quoteFilterValue` wrapper is load-bearing in a way that is easy to
 * "simplify" away: PostgREST unescapes backslashes inside an `or=` group, so
 * quoting — which doubles them — is exactly what lets `ilikePattern`'s `\%` and
 * `\_` survive to reach LIKE. Drop the quoting and a search for `100%` or
 * `snake_case` silently matches the entire library instead.
 *
 * Verified against production PostgREST through supabase-js: searching for a
 * literal `_` returns only the rows that contain one, not every row.
 */
export function textSearchFilter(
  query: string,
  columns: readonly string[] = SEARCH_TEXT_COLUMNS
): string {
  const pattern = quoteFilterValue(ilikePattern(query));
  return columns.map((c) => `${c}.ilike.${pattern}`).join(",");
}

/**
 * Which of the user's known labels contain the query as a substring.
 *
 * Postgres can't pattern-match inside an array without unnesting, so instead of
 * an RPC we resolve the substring against the label vocabulary we already hold
 * client-side and then ask for cards overlapping the resulting exact tags.
 */
export function labelsMatching(query: string, allLabels: string[]): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return allLabels.filter((l) => l.toLowerCase().includes(q));
}

/**
 * Substring match over the cards already in memory.
 *
 * Mirrors the fields the server query searches, so instant local feedback and
 * the authoritative result set agree about what counts as a match.
 */
export function searchLoadedThoughts(query: string, thoughts: Thought[]): Thought[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return thoughts.filter(
    (t) =>
      (t.title && t.title.toLowerCase().includes(q)) ||
      t.body.toLowerCase().includes(q) ||
      (t.labels ?? []).some((label) => label.toLowerCase().includes(q)) ||
      (t.source_title && t.source_title.toLowerCase().includes(q))
  );
}

export interface ThoughtSearchResult {
  results: Thought[];
  /** More matches exist than `SEARCH_LIMIT` — the UI says so rather than lying by omission. */
  truncated: boolean;
  /** False when the server leg failed and these are in-memory results only. */
  complete: boolean;
}

/**
 * Newest first, ties broken by id — the same total order the DB queries use.
 *
 * The tiebreak compares by code point, not `localeCompare`: ids are uuids, which
 * Postgres orders bytewise, and locale-aware collation can treat the hyphens as
 * ignorable punctuation. Client and server order must agree, or a card merged in
 * locally lands in a different position than the one the server would page it in.
 */
export function byNewest(a: Pick<Thought, "created_at" | "id">, b: Pick<Thought, "created_at" | "id">): number {
  const delta = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  if (delta !== 0) return delta;
  if (a.id === b.id) return 0;
  return a.id < b.id ? 1 : -1;
}

/** First occurrence of each id wins, preserving order. */
export function dedupeById<T extends { id: string }>(rows: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

export interface ConnectionRow {
  from_thought_id: string;
  to_thought_id: string;
}

/** Undirected per-card connection counts from raw `connections` rows. */
export function countConnections(rows: ConnectionRow[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const c of rows) {
    counts.set(c.from_thought_id, (counts.get(c.from_thought_id) ?? 0) + 1);
    counts.set(c.to_thought_id, (counts.get(c.to_thought_id) ?? 0) + 1);
  }
  return counts;
}

/** Shape of a `thoughts` row as selected by `THOUGHT_COLUMNS`. */
export interface ThoughtRow {
  id: string;
  title: string | null;
  body: string;
  source_url: string | null;
  source_title: string | null;
  labels: string[] | null;
  created_at: string;
  updated_at: string | null;
  is_public: boolean | null;
  visibility: "private" | "public" | null;
  source_card_id: string | null;
  image_path: string | null;
  notes: string | null;
  link_preview: Thought["link_preview"] | null;
}

/** The one DB-row -> UI-Thought mapping. */
export function rowToThought(row: ThoughtRow, connections = 0): Thought {
  return {
    id: row.id,
    title: row.title ?? undefined,
    body: row.body,
    source_url: row.source_url ?? undefined,
    source_title: row.source_title ?? undefined,
    labels: row.labels ?? [],
    connections,
    created_at: row.created_at,
    // `updated_at` equals `created_at` until the first real edit; surfacing an
    // "edited" timestamp for an untouched card would be a lie.
    edited_at: row.updated_at && row.updated_at !== row.created_at ? row.updated_at : undefined,
    image_path: row.image_path ?? undefined,
    notes: row.notes ?? undefined,
    author: "You",
    visibility: row.visibility ?? (row.is_public ? "public" : "private"),
    source_card_id: row.source_card_id ?? undefined,
    link_preview: row.link_preview ?? undefined,
  };
}

/** Distinct, case-insensitively de-duplicated labels, alphabetised. */
export function distinctLabels(rows: { labels: string[] | null }[]): string[] {
  const bySlug = new Map<string, string>();
  for (const row of rows) {
    for (const label of row.labels ?? []) {
      if (!label) continue;
      const key = label.toLowerCase();
      if (!bySlug.has(key)) bySlug.set(key, label);
    }
  }
  return Array.from(bySlug.values()).sort((a, b) => a.localeCompare(b));
}

export interface PageResult<T> {
  data: T[] | null;
  error: unknown;
}

/**
 * Walk an offset-paged query to completion.
 *
 * The stop condition is a short page, which is the only signal that can't lie:
 * a total count can go stale mid-walk, and trusting one risks either dropping
 * the tail or looping forever. `maxRows` is a runaway guard, not a product
 * limit — `complete` reports honestly whether the walk actually finished so
 * callers can say so rather than quietly presenting a partial set as whole.
 */
export async function pageAll<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
  opts: { pageSize?: number; maxRows?: number; onPage?: (rows: T[]) => void } = {}
): Promise<{ rows: T[]; complete: boolean }> {
  const pageSize = opts.pageSize ?? PAGE_SIZE;
  const maxRows = opts.maxRows ?? Infinity;
  const out: T[] = [];

  for (let from = 0; ; from += pageSize) {
    const { data, error } = await fetchPage(from, from + pageSize - 1);
    if (error) throw error;
    const rows = data ?? [];
    out.push(...rows);
    if (rows.length > 0) opts.onPage?.(rows);
    if (rows.length < pageSize) return { rows: out, complete: true };
    if (out.length >= maxRows) return { rows: out, complete: false };
  }
}
